import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { telHref, waHref } from '@/lib/seo';
import { NAV_LINKS } from './Navbar';

/** TikTok is not in lucide-react; a small inline glyph keeps the icon set consistent. */
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true"><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.6 2.6 0 0 1-2.6-2.6 2.6 2.6 0 0 1 3.38-2.48V9.66a5.7 5.7 0 0 0-.78-.06 5.7 5.7 0 0 0-5.7 5.7 5.7 5.7 0 0 0 5.7 5.7 5.7 5.7 0 0 0 5.7-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.3 4.3 0 0 1-3.26-1.48Z" /></svg>
);

export const Footer = () => {
  const { site } = useSite();
  const s = site?.settings || {};
  const links = [...NAV_LINKS.map(({ to, label }) => ({ to, label })), { to: '/getting-here', label: 'Getting here' }, { to: '/contact', label: 'Contact us' }];
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-background" data-testid="site-footer">
      <div className="container-x pt-16 pb-14 sm:pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <img src="/logos/bukit-ancak.png" alt="Bukit Ancak Lembongan Resort" className="h-14 w-auto" loading="lazy" width="200" height="56" />
            {s.footer_text && <p className="mt-6 text-sm leading-relaxed text-foreground/70 max-w-sm">{s.footer_text}</p>}
            <div className="mt-6 space-y-2.5 text-sm text-foreground/70">
              {s.address && (
                <p className="flex gap-2.5"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-teal))]" />{s.address}</p>
              )}
              {s.phone && (
                <p className="flex gap-2.5"><Phone className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-teal))]" /><a href={telHref(s.phone)} className="hover:text-foreground" aria-label={`Call Bukit Ancak on ${s.phone}`} data-testid="footer-phone-link">{s.phone}</a></p>
              )}
              {s.whatsapp_number && (
                <p className="flex gap-2.5"><MessageCircle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-teal))]" /><a href={waHref(s.whatsapp_number)} target="_blank" rel="noopener noreferrer" className="hover:text-foreground" aria-label="Chat with Bukit Ancak on WhatsApp" data-testid="footer-whatsapp-link">WhatsApp us</a></p>
              )}
              {s.email && (
                <p className="flex gap-2.5"><Mail className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-teal))]" /><a href={`mailto:${s.email}`} className="hover:text-foreground">{s.email}</a></p>
              )}
            </div>
          </div>

          <div className="md:col-span-4 md:col-start-7">
            <p className="eyebrow text-[hsl(var(--brand-teal))] mb-5">Explore</p>
            <ul className={`grid gap-x-8 gap-y-3 ${links.length > 5 ? 'grid-cols-2' : 'grid-cols-1'}`} data-testid="footer-menu">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-[12px] tracking-[0.14em] uppercase text-foreground/65 hover:text-foreground transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow text-[hsl(var(--brand-teal))] mb-5">Follow</p>
            <div className="flex gap-3">
              {s.instagram && (
                <a href={s.instagram} target="_blank" rel="noopener noreferrer" aria-label="Bukit Ancak Lembongan on Instagram (opens in a new tab)" data-testid="footer-instagram-link" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 transition-colors"><Instagram className="h-4 w-4" /></a>
              )}
              {s.facebook && (
                <a href={s.facebook} target="_blank" rel="noopener noreferrer" aria-label="Bukit Ancak Lembongan on Facebook (opens in a new tab)" data-testid="footer-facebook-link" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 transition-colors"><Facebook className="h-4 w-4" /></a>
              )}
              {s.tiktok && (
                <a href={s.tiktok} target="_blank" rel="noopener noreferrer" aria-label="Bukit Ancak Lembongan on TikTok (opens in a new tab)" data-testid="footer-tiktok-link" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 transition-colors"><TikTokIcon className="h-4 w-4" /></a>
              )}
              {!s.instagram && !s.facebook && !s.tiktok && <p className="text-sm text-muted-foreground">Nusa Lembongan, Bali</p>}
            </div>
            <a href={s.reserve_url || '#'} target="_blank" rel="noopener noreferrer" className="btn-outline mt-8 w-full md:w-auto" aria-label="Reserve a room at Bukit Ancak Lembongan (opens booking site in a new tab)" data-testid="footer-reserve-button">Reserve</a>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[11px] tracking-[0.12em] uppercase text-foreground/50 pr-16 sm:pr-0">
          <p>&copy; {year} {s.site_name || 'Bukit Ancak Lembongan Resort'}</p>
          <p className="sm:text-right normal-case tracking-normal text-[11px] text-foreground/55 leading-relaxed" data-testid="footer-operator-credit">
            {s.operator_prefix ?? 'Fully managed and operated by'}{' '}
            <a href={s.operator_url || 'https://armellabali.com'} target="_blank" rel="noopener noreferrer" className="text-foreground/75 hover:text-foreground underline-offset-4 hover:underline transition-colors" data-testid="footer-operator-link">
              {s.operator_name || 'PT Armella Management Indonesia'}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
